import React, { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from '../../../hooks/storeHooks';
import Loader from '../../../features/common/Loader/Loader';
import * as am5 from '@amcharts/amcharts5';
import { createDualYAxisChart } from '../../../utilities/DualYChartUtils';
import './DualYTimeSeriesChart.scss';
import { getSortedChartData } from '../../../utilities/chartHelpers';
import EmptyState from '../../empty-state/EmptyState';

interface DualYTimeSeriesDataProps {
  timeseriesChartData: any;
}

const DualYTimeSeriesChart: React.FC<DualYTimeSeriesDataProps> = ({ timeseriesChartData }) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<am5.Root | null>(null);
  const chartData = useAppSelector((state: any) => state.AssetTrendDataSlice.data);
  const loading = useAppSelector((state: any) => state.AssetTrendDataSlice.loading);
  const parameterData = timeseriesChartData?.widgetProperty.widgetProperties.parameterConfig[0]?.parameters;
  const chartViewOptions = useAppSelector((state: any) => state.AssetTrendDataSlice.chartViewOptions);
  const downTimeSliceData = useAppSelector((state: any) => state.shutdownAndDropdownSlice?.data?.downtimeData ?? []);
  const sortedData = useMemo(() => getSortedChartData(chartData, parameterData), [chartData, parameterData]);
  const hasPlottableData = sortedData.some((d: any) => Array.isArray(d.dataPoints) && d.dataPoints.length > 0);
  const pumpCheckerEventsSlice = useAppSelector((state: any) => state.PumpCheckerEventsSlice.data);
  const startDate = useAppSelector((state: any) => state.AssetTrendDataSlice.startDate);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);

  // Cleanup: Dispose chart root when component unmounts
  useEffect(() => {
    return () => {
      if (rootRef.current) {
        try {
          rootRef.current.dispose();
        } catch (e) {
          console.error('Error disposing chart root on unmount:', e);
        }
        rootRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0 || !parameterData || parameterData.length === 0 || !chartRef.current) {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
      return;
    }

    // Dispose previous chart before creating a new one
    if (rootRef.current) {
      rootRef.current.dispose();
      rootRef.current = null;
    }

    try {
      const newRoot = createDualYAxisChart(
        chartRef.current,
        sortedData,
        parameterData,
        chartViewOptions,
        downTimeSliceData,
        pumpCheckerEventsSlice,
        startDate,
        endDate,
      );
      if (newRoot) {
        rootRef.current = newRoot;
      }
    } catch (e) {
      console.error('Error creating dual Y axis chart:', e);
    }

    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
      }
    };
  }, [chartData, chartViewOptions, timeseriesChartData, pumpCheckerEventsSlice, downTimeSliceData]);

  if (loading) return <Loader />;
  if (!hasPlottableData)
    return (
      <div className='empty-data-container'>
        <EmptyState />
      </div>
    );

  return <div ref={chartRef} className='dual-y-axis-chart-container' />;
};

export default DualYTimeSeriesChart;
